import { FinancialRecord } from '../types/data/financialRecord';

// Define chart configuration types
export interface ChartConfig {
  type: 'bar' | 'line' | 'pie' | 'scatter' | 'area';
  title: string;
  dataFields: string[];
  labels?: string[];
  colors?: string[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  transformations?: string[];
}

/**
 * Parse the AI response to extract chart configuration
 */
export function parseAIResponse(aiResponse: string): ChartConfig[] {
  try {
    // Basic implementation to extract chart configs
    // In a real-world scenario, this would be more sophisticated
    const charts: ChartConfig[] = [];
    
    // Look for chart type declarations
    const chartTypeRegex = /type of chart to use:?\s*(bar|line|pie|scatter|area)/gi;
    const titleRegex = /title:?\s*"([^"]+)"/gi;
    const dataFieldsRegex = /data fields:?\s*\[([^\]]+)\]/gi;
    const colorsRegex = /colors?:?\s*\[([^\]]+)\]/gi;
    const xAxisRegex = /x[\s-]axis:?\s*"([^"]+)"/gi;
    const yAxisRegex = /y[\s-]axis:?\s*"([^"]+)"/gi;
    
    // Simple extraction - would be improved in production
    let chartType = chartTypeRegex.exec(aiResponse)?.[1].toLowerCase() as ChartConfig['type'] || 'bar';
    let title = titleRegex.exec(aiResponse)?.[1] || 'Financial Data Visualization';
    
    // Extract data fields
    let dataFieldsMatch = dataFieldsRegex.exec(aiResponse);
    let dataFields: string[] = [];
    
    if (dataFieldsMatch && dataFieldsMatch[1]) {
      dataFields = dataFieldsMatch[1].split(',').map(field => 
        field.trim().replace(/["']/g, '')
      );
    } else {
      // Fallback to looking for field names in the text
      const fieldNameRegex = /(newVehicleSalesRevenue|usedVehicleSalesRevenue|dealerName|brand|year|month|totalDirectProfit)/gi;
      const matches = aiResponse.match(fieldNameRegex) || [];
      dataFields = Array.from(new Set(matches.map(m => m.trim())));
    }
    
    // Extract colors if available
    let colorsMatch = colorsRegex.exec(aiResponse);
    let colors: string[] = [];
    
    if (colorsMatch && colorsMatch[1]) {
      colors = colorsMatch[1].split(',').map(color => 
        color.trim().replace(/["']/g, '')
      );
    } else {
      // Default colors
      colors = ['#4285F4', '#34A853', '#FBBC05', '#EA4335', '#8448FF'];
    }
    
    // Extract axis labels if available
    const xAxisLabel = xAxisRegex.exec(aiResponse)?.[1] || '';
    const yAxisLabel = yAxisRegex.exec(aiResponse)?.[1] || '';
    
    charts.push({
      type: chartType,
      title,
      dataFields,
      colors,
      xAxisLabel,
      yAxisLabel
    });
    
    return charts;
  } catch (error) {
    console.error('Error parsing AI response:', error);
    return [{
      type: 'bar',
      title: 'Financial Data',
      dataFields: ['dealerName', 'totalDirectProfit'],
      colors: ['#4285F4', '#34A853']
    }];
  }
}

/**
 * Generate chart data from financial records based on chart configuration
 */
export function generateChartData(records: FinancialRecord[], config: ChartConfig): any[] {
  if (!records || records.length === 0 || !config) {
    return [];
  }

  try {
    // For simplicity, we'll implement a basic data transformation
    // In a real-world app, this would be more sophisticated
    
    // If we're grouping by a category (like dealerName or brand)
    if (config.dataFields.includes('dealerName') || config.dataFields.includes('brand')) {
      const groupByField = config.dataFields.includes('dealerName') ? 'dealerName' : 'brand';
      const valueFields = config.dataFields.filter(field => field !== groupByField);
      
      // Group the data
      const groupedData: Record<string, FinancialRecord[]> = {};
      records.forEach(record => {
        const key = record[groupByField as keyof FinancialRecord] as string || 'Unknown';
        if (!groupedData[key]) {
          groupedData[key] = [];
        }
        groupedData[key].push(record);
      });
      
      // Calculate aggregates for each group
      return Object.keys(groupedData).map(key => {
        const group = groupedData[key];
        const result: any = { [groupByField]: key };
        
        valueFields.forEach(field => {
          // Sum numeric values
          result[field] = group.reduce((sum, record) => {
            const value = record[field as keyof FinancialRecord];
            return sum + (typeof value === 'number' ? value : Number(value) || 0);
          }, 0);
        });
        
        return result;
      });
    }
    
    // If we're looking at time series (by year/month)
    else if (config.dataFields.includes('year') || config.dataFields.includes('month')) {
      const timeFields: string[] = [];
      if (config.dataFields.includes('year')) timeFields.push('year');
      if (config.dataFields.includes('month')) timeFields.push('month');
      
      const valueFields = config.dataFields.filter(field => 
        !timeFields.includes(field)
      );
      
      // Group by time periods
      const groupedData: Record<string, FinancialRecord[]> = {};
      records.forEach(record => {
        const key = timeFields.map(field => 
          record[field as keyof FinancialRecord]
        ).join('-');
        
        if (!groupedData[key]) {
          groupedData[key] = [];
        }
        groupedData[key].push(record);
      });
      
      // Calculate aggregates for each time period
      return Object.keys(groupedData).map(key => {
        const group = groupedData[key];
        const result: any = {};
        
        // Add time period fields
        const keyParts = key.split('-');
        timeFields.forEach((field, index) => {
          result[field] = keyParts[index];
        });
        
        // Add aggregated values
        valueFields.forEach(field => {
          result[field] = group.reduce((sum, record) => {
            const value = record[field as keyof FinancialRecord];
            return sum + (typeof value === 'number' ? value : Number(value) || 0);
          }, 0);
        });
        
        return result;
      });
    }
    
    // Fallback - just return the raw records
    return records.map(record => {
      const result: any = {};
      config.dataFields.forEach(field => {
        result[field] = record[field as keyof FinancialRecord];
      });
      return result;
    });
  } catch (error) {
    console.error('Error generating chart data:', error);
    return [];
  }
}

/**
 * Generate HTML for chart visualization
 */
export function generateChartHtml(records: FinancialRecord[], aiResponse: string): string {
  try {
    const chartConfigs = parseAIResponse(aiResponse);
    
    // Create HTML with embedded chart.js
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>VisuAI Data Visualization</title>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
        <style>
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f8f9fa;
          }
          .chart-container { 
            width: 90%;
            max-width: 1200px;
            margin: 20px auto;
            padding: 20px;
            background-color: white;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          h1 {
            text-align: center;
            color: #2d3748;
            margin-bottom: 30px;
          }
          pre {
            background-color: #f1f1f1;
            padding: 15px;
            border-radius: 4px;
            overflow: auto;
            margin-top: 30px;
          }
          .chart-title {
            font-size: 1.2rem;
            font-weight: 600;
            margin-bottom: 15px;
            color: #4a5568;
          }
          .response-container {
            margin-top: 40px;
            padding: 20px;
            background-color: #f1f1f1;
            border-radius: 8px;
          }
          .response-title {
            font-size: 1.2rem;
            font-weight: 600;
            margin-bottom: 15px;
            color: #4a5568;
          }
          .response-content {
            white-space: pre-wrap;
            font-family: monospace;
            font-size: 0.9rem;
          }
        </style>
      </head>
      <body>
        <h1>VisuAI Data Visualization</h1>
    `;
    
    // Add chart containers
    chartConfigs.forEach((config, index) => {
      html += `
        <div class="chart-container">
          <div class="chart-title">${config.title}</div>
          <canvas id="chart-${index}"></canvas>
        </div>
      `;
    });
    
    // Add original Claude response for reference
    html += `
      <div class="response-container">
        <div class="response-title">AI Visualization Response</div>
        <div class="response-content">${aiResponse}</div>
      </div>
    `;
    
    // Add JavaScript to initialize charts
    html += `
      <script>
        document.addEventListener('DOMContentLoaded', function() {
          const chartColors = ${JSON.stringify(chartConfigs.map(c => c.colors || ['#4285F4', '#34A853', '#FBBC05', '#EA4335', '#8448FF']))};
          const chartData = ${JSON.stringify(chartConfigs.map(config => generateChartData(records, config)))};
          const chartConfigs = ${JSON.stringify(chartConfigs)};
          
          // Create each chart
          chartConfigs.forEach((config, index) => {
            const ctx = document.getElementById('chart-' + index).getContext('2d');
            
            // Prepare datasets based on config
            const datasets = [];
            const data = chartData[index];
            
            if (!data || data.length === 0) {
              // Show no data message
              const noDataMsg = document.createElement('div');
              noDataMsg.textContent = 'No data available for this chart';
              noDataMsg.style.textAlign = 'center';
              noDataMsg.style.padding = '40px';
              noDataMsg.style.color = '#999';
              document.getElementById('chart-' + index).parentNode.replaceChild(noDataMsg, document.getElementById('chart-' + index));
              return;
            }
            
            // Get all keys that aren't grouping fields
            const firstDataPoint = data[0];
            const valueKeys = Object.keys(firstDataPoint).filter(key => 
              key !== 'dealerName' && key !== 'brand' && key !== 'year' && key !== 'month'
            );
            
            // For pie charts
            if (config.type === 'pie') {
              const labels = data.map(d => d[config.dataFields[0]]);
              const values = data.map(d => d[config.dataFields[1]]);
              
              new Chart(ctx, {
                type: 'pie',
                data: {
                  labels: labels,
                  datasets: [{
                    data: values,
                    backgroundColor: config.colors || chartColors[index],
                    borderWidth: 1
                  }]
                },
                options: {
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'right',
                    },
                    title: {
                      display: true,
                      text: config.title
                    }
                  }
                }
              });
            } 
            // For bar/line/area charts
            else {
              let labels;
              let xField;
              
              // Determine x-axis field
              if (data[0].hasOwnProperty('dealerName')) xField = 'dealerName';
              else if (data[0].hasOwnProperty('brand')) xField = 'brand';
              else if (data[0].hasOwnProperty('year') && data[0].hasOwnProperty('month')) {
                xField = 'yearMonth';
                // Create combined year-month labels
                data.forEach(d => {
                  d.yearMonth = d.year + '-' + d.month;
                });
              }
              else if (data[0].hasOwnProperty('year')) xField = 'year';
              else if (data[0].hasOwnProperty('month')) xField = 'month';
              else xField = Object.keys(data[0])[0];
              
              labels = data.map(d => d[xField]);
              
              // Create datasets for each value field
              valueKeys.forEach((key, i) => {
                if (key !== xField) {
                  datasets.push({
                    label: key,
                    data: data.map(d => d[key]),
                    backgroundColor: config.colors?.[i] || chartColors[index][i % chartColors[index].length],
                    borderColor: config.colors?.[i] || chartColors[index][i % chartColors[index].length],
                    borderWidth: 1,
                    fill: config.type === 'area'
                  });
                }
              });
              
              // Create the chart
              new Chart(ctx, {
                type: config.type === 'area' ? 'line' : config.type,
                data: {
                  labels: labels,
                  datasets: datasets
                },
                options: {
                  responsive: true,
                  scales: {
                    x: {
                      title: {
                        display: !!config.xAxisLabel,
                        text: config.xAxisLabel
                      }
                    },
                    y: {
                      beginAtZero: true,
                      title: {
                        display: !!config.yAxisLabel,
                        text: config.yAxisLabel
                      }
                    }
                  },
                  plugins: {
                    legend: {
                      position: 'top',
                    },
                    title: {
                      display: true,
                      text: config.title
                    }
                  }
                }
              });
            }
          });
        });
      </script>
      </body>
      </html>
    `;
    
    return html;
  } catch (error) {
    console.error('Error generating chart HTML:', error);
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>VisuAI Error</title>
        <style>
          body { 
            font-family: sans-serif; 
            display: flex; 
            justify-content: center; 
            align-items: center; 
            height: 100vh; 
            margin: 0;
            background-color: #f8f9fa;
          }
          .error { 
            padding: 30px; 
            background-color: #fff5f5; 
            border-left: 5px solid #f56565; 
            border-radius: 5px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          h2 { 
            margin-top: 0; 
            color: #c53030;
          }
          pre { 
            background-color: #f5f5f5; 
            padding: 15px; 
            overflow: auto; 
            border-radius: 4px;
          }
        </style>
      </head>
      <body>
        <div class="error">
          <h2>Error Generating Visualization</h2>
          <p>There was an error processing the AI response or generating the chart.</p>
          <pre>${String(error)}</pre>
          <pre>${aiResponse}</pre>
        </div>
      </body>
      </html>
    `;
  }
} 